'use client'

import { useState, useEffect } from 'react'
import { Star } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { Container } from '@/components/ui/container'
import { Section } from '@/components/ui/section'
import { Eyebrow } from '@/components/ui/eyebrow'

const gradients = [
  'linear-gradient(135deg, #2B5AFF, #4F35D6)',
  'linear-gradient(135deg, #7C2FE8, #A020C8)',
  'linear-gradient(135deg, #00C2FF, #2B5AFF)',
]

export function Testimonials() {
  const t = useTranslations('home.testimonials')
  const items = t.raw('items') as Array<{
    name: string
    role: string
    company: string
    content: string
    initials: string
  }>

  const [active, setActive] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((i) => (i + 1) % items.length)
    }, 5000)
    return () => clearInterval(timer)
  }, [items.length])

  return (
    <Section id="testimonials" tone="white" spacing="lg">
      <Container>
        <div className="grid items-center gap-16 lg:grid-cols-2">
          {/* Left */}
          <div className="flex flex-col gap-6">
            <Eyebrow>{t('eyebrow')}</Eyebrow>
            <h2 className="font-display text-[clamp(1.8rem,4vw,3.2rem)] font-bold tracking-[-0.025em] text-text-primary">
              {t('title')}
            </h2>
            <p className="text-[17px] leading-[1.75] text-text-muted">
              {t('subtitle')}
            </p>
            <div className="mt-2 flex gap-2">
              {items.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActive(i)}
                  aria-label={`Go to testimonial ${i + 1}`}
                  aria-current={i === active ? 'true' : undefined}
                  className={`h-2 rounded-full transition-all duration-300 ${i === active ? 'w-8 bg-[#2B5AFF]' : 'w-2 bg-slate-300'}`}
                />
              ))}
            </div>
          </div>

          {/* Right */}
          <div className="relative h-[300px] overflow-visible">
            {/* Decorative corner accents */}
            <div className="absolute h-24 w-24 rounded-xl bg-[rgba(43,90,255,0.05)]" style={{ zIndex: 1, right: -24, top: -24 }} />
            <div className="absolute h-24 w-24 rounded-xl bg-[rgba(43,90,255,0.05)]" style={{ zIndex: 1, left: -24, bottom: -24 }} />
            {items.map((item, i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: '#fff',
                  border: '1px solid #E2E8F0',
                  borderRadius: 16,
                  padding: 32,
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                  transition: 'opacity 0.5s ease, transform 0.5s ease',
                  opacity: i === active ? 1 : 0,
                  transform: i === active ? 'translateX(0) scale(1)' : 'translateX(80px) scale(0.95)',
                  zIndex: i === active ? 20 : 2,
                  pointerEvents: i === active ? 'auto' : 'none',
                }}
              >
                <div className="mb-4 flex gap-1">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star key={j} size={18} fill="#EAB308" stroke="#EAB308" />
                  ))}
                </div>
                <p className="flex-1 text-base font-medium leading-[1.7] text-text-primary">
                  &ldquo;{item.content}&rdquo;
                </p>
                <div className="my-5 h-px w-full bg-[#E2E8F0]" />
                <div className="flex items-center gap-4">
                  <div
                    aria-hidden="true"
                    className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-[#E2E8F0] text-sm font-bold text-white"
                    style={{ background: gradients[i % gradients.length] }}
                  >
                    {item.initials}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-text-primary">{item.name}</p>
                    <p className="text-xs text-text-muted">{item.role}, {item.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </Section>
  )
}
